/**
 * ONE resolver for "what does the pointer point at, in editing terms" — used by BOTH the canvas click
 * handler (which SELECTS it) and the hover frame (which only OUTLINES it). Sharing it is the whole point:
 * the dashed frame you see on hover is, by construction, exactly what a click on that pixel will select.
 *
 * Precedence, mirroring Shopify's drill-in:
 *   • A `data-aqx-field-id` hotspot (a setting-bound element — a heading, a CTA) beats a
 *     `data-aqx-block-id` block. A field nested INSIDE a block is that block's setting; a bare field is a
 *     section setting (the common case).
 *   • Fields/blocks only resolve when we're ACTIVE on that section: select mode is on (toolbar toggle), or
 *     the section is already selected (1st click selects the section, the 2nd refines to the part).
 *   • Otherwise the whole SECTION is the target.
 */
export type CanvasTarget =
  | { kind: 'field'; id: string; sectionId: string; blockId: string | null; el: Element }
  | { kind: 'block'; id: string; sectionId: string; blockId: string; el: Element }
  | { kind: 'section'; id: string; sectionId: string; blockId: null; el: Element };

export function resolveCanvasTarget(
  from: Element | null,
  opts: { selectMode: boolean; selectedSectionId: string | null },
): CanvasTarget | null {
  if (!from) return null;
  const sectionEl = from.closest('[data-section-id]');
  const sectionId = sectionEl?.getAttribute('data-section-id');
  if (!sectionEl || !sectionId) return null;

  const active = opts.selectMode || opts.selectedSectionId === sectionId;
  if (active) {
    const fieldEl = from.closest('[data-aqx-field-id]');
    const blockEl = from.closest('[data-aqx-block-id]');

    if (fieldEl && sectionEl.contains(fieldEl)) {
      const fieldId = fieldEl.getAttribute('data-aqx-field-id');
      if (fieldId) {
        const ownerBlockId =
          blockEl && blockEl.contains(fieldEl) ? blockEl.getAttribute('data-aqx-block-id') : null;
        return { kind: 'field', id: fieldId, sectionId, blockId: ownerBlockId, el: fieldEl };
      }
    }

    if (blockEl && sectionEl.contains(blockEl)) {
      const blockId = blockEl.getAttribute('data-aqx-block-id');
      if (blockId) return { kind: 'block', id: blockId, sectionId, blockId, el: blockEl };
    }
  }

  return { kind: 'section', id: sectionId, sectionId, blockId: null, el: sectionEl };
}

/** True when the target is ALREADY the current selection (the solid frame owns it — don't double-draw). */
export function isCurrentSelection(
  t: CanvasTarget,
  sel: {
    selectedSectionId: string | null;
    selectedBlockId: string | null;
    focusedFieldId: string | null;
  },
): boolean {
  if (t.kind === 'field')
    return sel.focusedFieldId === t.id && sel.selectedSectionId === t.sectionId;
  if (t.kind === 'block') return !sel.focusedFieldId && sel.selectedBlockId === t.id;
  return !sel.focusedFieldId && !sel.selectedBlockId && sel.selectedSectionId === t.id;
}
